/**
 * PaymentsLedger.js — Bank Statement Style Payments Dashboard
 *
 * Roles: owner, solution, admin, supervisor, finance
 * Shows: summary chips, charts, pending online confirmations, transaction ledger
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import api from '../api';

var URL = process.env.REACT_APP_URL;

/* ── helpers ──────────────────────────────────────────── */
function fmtCurrency(n) {
  if (n === undefined || n === null) return '\u20B90';
  var num = Math.round(Number(n));
  if (num >= 100000) return '\u20B9' + (num / 100000).toFixed(1) + 'L';
  if (num >= 1000)   return '\u20B9' + (num / 1000).toFixed(1) + 'K';
  return '\u20B9' + num.toLocaleString('en-IN');
}
function fmtFull(n) {
  return '\u20B9' + Math.round(Number(n || 0)).toLocaleString('en-IN');
}
function fmtDate(d) {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
function fmtDateTime(d) {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ' ' + new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function timeAgo(d) {
  var ms = Date.now() - new Date(d).getTime();
  var mins = Math.floor(ms / 60000);
  if (mins < 60) return mins + 'm ago';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ' + (mins % 60) + 'm ago';
  return Math.floor(hrs / 24) + 'd ago';
}
function urgencyColor(d) {
  var hrs = (Date.now() - new Date(d).getTime()) / 3600000;
  if (hrs < 1) return '#059669';
  if (hrs < 2) return '#d97706';
  return '#dc2626';
}

var PIE_COLORS = ['#059669', '#6366f1', '#0891b2', '#f59e0b', '#ef4444'];

/* ── Chip Card ─────────────────────────────────────────── */
function ChipCard({ icon, label, value, gradient, sub }) {
  return (
    <div style={{
      flex: '1 1 140px', minWidth: 130, padding: '16px 14px', borderRadius: 14,
      background: gradient, color: '#fff',
      boxShadow: '0 2px 8px rgba(0,0,0,.12)',
    }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "'SF Mono', Menlo, monospace" }}>{value}</div>
      <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
export default function PaymentsLedger() {
  // Date range (default: current month)
  var now = new Date();
  var monthStart = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-01';
  var todayStr = now.toISOString().split('T')[0];

  var [fromDate, setFromDate] = useState(monthStart);
  var [toDate, setToDate]     = useState(todayStr);
  var [orgs, setOrgs]         = useState([]);
  var [activeOrg, setActiveOrg] = useState(''); // '' = all
  var [summary, setSummary]   = useState(null);
  var [chartData, setChartData] = useState(null);
  var [pending, setPending]   = useState([]);
  var [ledger, setLedger]     = useState([]);
  var [ledgerTotal, setLedgerTotal] = useState(0);
  var [ledgerPage, setLedgerPage] = useState(1);
  var [loading, setLoading]   = useState(true);
  var [confirming, setConfirming] = useState(null);
  var [rejecting, setRejecting]   = useState(null);

  /* ── Load orgs on mount ── */
  useEffect(function() {
    api.get(URL + '/orgs')
      .then(function(r) { setOrgs((r.data || []).filter(function(o) { return o.status === 'active' || !o.status; })); })
      .catch(function() {});
  }, []);

  /* ── Fetch all data ── */
  var fetchAll = useCallback(function() {
    setLoading(true);
    var params = 'from=' + fromDate + '&to=' + toDate + (activeOrg ? '&org=' + encodeURIComponent(activeOrg) : '');

    Promise.all([
      api.get(URL + '/payments/summary?' + params).catch(function() { return { data: {} }; }),
      api.get(URL + '/payments/chart-data?' + params).catch(function() { return { data: {} }; }),
      api.get(URL + '/payments/pending-online').catch(function() { return { data: [] }; }),
      api.get(URL + '/payments/ledger?' + params + '&page=' + ledgerPage + '&limit=50').catch(function() { return { data: { rows: [], total: 0 } }; }),
    ]).then(function(results) {
      setSummary(results[0].data);
      setChartData(results[1].data);
      setPending(results[2].data || []);
      setLedger(results[3].data.rows || []);
      setLedgerTotal(results[3].data.total || 0);
      setLoading(false);
    });
  }, [fromDate, toDate, activeOrg, ledgerPage]);

  useEffect(function() { fetchAll(); }, [fetchAll]);

  /* ── Confirm payment ── */
  function handleConfirm(appt) {
    var amountStr = prompt('Enter payment amount (\u20B9):', '');
    if (!amountStr) return;
    var amount = Number(amountStr);
    if (isNaN(amount) || amount <= 0) { alert('Invalid amount'); return; }

    setConfirming(appt._id);
    api.put(URL + '/payments/confirm/' + appt._id, { amount: amount, org: activeOrg || 'MedicalyogaTherapyCentre', serviceName: 'Online Booking Payment' })
      .then(function(r) {
        var data = r.data;
        // Open WhatsApp with receipt
        if (data.patientPhone && data.receiptText) {
          var phone = data.patientPhone.replace(/\D/g, '');
          if (phone.length === 10) phone = '91' + phone;
          window.open('https://wa.me/' + phone + '?text=' + encodeURIComponent(data.receiptText), '_blank', 'noopener,noreferrer');
        }
        fetchAll();
      })
      .catch(function(e) { alert('Confirm error: ' + (e.response ? e.response.data.error : e.message)); })
      .finally(function() { setConfirming(null); });
  }

  function handleReject(appt) {
    if (!window.confirm('Reject payment from ' + (appt.name || appt.patientCard) + '? This will cancel the appointment.')) return;
    setRejecting(appt._id);
    api.put(URL + '/payments/reject/' + appt._id)
      .then(function() { fetchAll(); })
      .catch(function(e) { alert('Reject error: ' + (e.response ? e.response.data.error : e.message)); })
      .finally(function() { setRejecting(null); });
  }

  /* ── Render ──────────────────────────────────────────── */
  var s = summary || {};

  return (
    <div style={{ padding: '20px 16px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#1e293b' }}>{'\uD83D\uDCB3'} Payments Ledger</h2>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Bank statement view of all payments</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={fromDate} onChange={function(e) { setFromDate(e.target.value); setLedgerPage(1); }}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" value={toDate} onChange={function(e) { setToDate(e.target.value); setLedgerPage(1); }}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
        </div>
      </div>

      {/* Org Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e2e8f0', overflowX: 'auto' }}>
        <button
          onClick={function() { setActiveOrg(''); setLedgerPage(1); }}
          style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
            background: 'transparent',
            color: activeOrg === '' ? '#2563eb' : '#64748b',
            borderBottom: activeOrg === '' ? '3px solid #2563eb' : '3px solid transparent',
            transition: 'all 0.15s',
          }}
        >All Orgs</button>
        {orgs.map(function(org) {
          var isActive = activeOrg === org.name;
          return (
            <button key={org._id || org.name}
              onClick={function() { setActiveOrg(org.name); setLedgerPage(1); }}
              style={{
                padding: '10px 20px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                background: 'transparent', whiteSpace: 'nowrap',
                color: isActive ? '#2563eb' : '#64748b',
                borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
                transition: 'all 0.15s',
              }}
            >{org.name || org.acr}</button>
          );
        })}
      </div>

      {loading && !summary ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>Loading payments data{'...'}</div>
      ) : (
        <>
          {/* ── Summary Chips ── */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            <ChipCard icon={'\uD83D\uDCB0'} label="Total Received" value={fmtCurrency(s.totalReceived)} gradient="linear-gradient(135deg,#059669,#10b981)" sub={s.billCount + ' bills'} />
            <ChipCard icon={'\uD83D\uDCB5'} label="Cash Received" value={fmtCurrency(s.cashReceived)} gradient="linear-gradient(135deg,#2563eb,#3b82f6)" />
            <ChipCard icon={'\uD83D\uDCF1'} label="Online Received" value={fmtCurrency(s.onlineReceived)} gradient="linear-gradient(135deg,#7c3aed,#8b5cf6)" />
            <ChipCard icon={'\u21A9\uFE0F'} label="Refunds Given" value={fmtCurrency(s.refundsGiven)} gradient="linear-gradient(135deg,#dc2626,#ef4444)" />
            <ChipCard icon={'\u23F3'} label="Pending Confirm" value={String(s.pendingOnline || 0)}
              gradient={s.pendingOnline > 0 ? 'linear-gradient(135deg,#d97706,#f59e0b)' : 'linear-gradient(135deg,#94a3b8,#cbd5e1)'}
              sub={s.pendingOnline > 0 ? 'Action required!' : 'All clear'} />
          </div>

          {/* ── Charts Row ── */}
          {chartData && (chartData.daily || []).length > 0 && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
              {/* Daily Collection Bar */}
              <div style={{ flex: '2 1 300px', background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Daily Collection</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData.daily}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={function(v) { return v.slice(5); }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={function(v) { return '\u20B9' + (v >= 1000 ? (v/1000) + 'K' : v); }} />
                    <Tooltip formatter={function(v) { return '\u20B9' + v.toLocaleString('en-IN'); }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="cash" stackId="a" fill="#2563eb" name="Cash" radius={[0,0,0,0]} />
                    <Bar dataKey="online" stackId="a" fill="#7c3aed" name="Online" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Payment Mode Pie */}
              <div style={{ flex: '1 1 200px', background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Payment Modes</div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={chartData.modeBreakdown || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={function(e) { return e.name; }}>
                      {(chartData.modeBreakdown || []).map(function(_, i) {
                        return <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />;
                      })}
                    </Pie>
                    <Tooltip formatter={function(v) { return '\u20B9' + v.toLocaleString('en-IN'); }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Trend Line */}
              <div style={{ flex: '2 1 300px', background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Cumulative Trend</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData.trend || []}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={function(v) { return v.slice(5); }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={function(v) { return '\u20B9' + (v >= 1000 ? Math.round(v/1000) + 'K' : v); }} />
                    <Tooltip formatter={function(v) { return '\u20B9' + v.toLocaleString('en-IN'); }} />
                    <Line type="monotone" dataKey="cumulative" stroke="#059669" strokeWidth={2.5} dot={false} name="Cumulative" />
                    <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={1.5} dot={false} name="Daily" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* ── Pending Online Confirmations ── */}
          {pending.length > 0 && (
            <div style={{
              background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 14,
              padding: 16, marginBottom: 24,
            }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#dc2626', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#dc2626', animation: 'blink 1s infinite' }} />
                Pending Online Confirmations ({pending.length})
              </div>
              <style>{'\n@keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }\n'}</style>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pending.map(function(a) {
                  var uc = urgencyColor(a.createdAt);
                  return (
                    <div key={a._id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                      background: '#fff', borderRadius: 10, padding: '10px 14px',
                      border: '1px solid #fecaca',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{a.name || a.patientCard}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {a.therapist} {'\u00B7'} {fmtDateTime(a.start)} {'\u00B7'} {a.isOnline ? 'Online' : 'At Centre'}
                        </div>
                      </div>
                      <div style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        color: uc, background: uc === '#059669' ? '#f0fdf4' : uc === '#d97706' ? '#fffbeb' : '#fef2f2',
                        border: '1px solid ' + uc,
                      }}>
                        {timeAgo(a.createdAt)}
                      </div>
                      <button
                        disabled={confirming === a._id}
                        onClick={function() { handleConfirm(a); }}
                        style={{
                          padding: '7px 14px', borderRadius: 8, border: 'none',
                          background: confirming === a._id ? '#d1d5db' : '#059669', color: '#fff',
                          fontWeight: 700, fontSize: 12, cursor: confirming === a._id ? 'default' : 'pointer',
                        }}
                      >{confirming === a._id ? '\u2026' : '\u2713 Confirm & Bill'}</button>
                      <button
                        disabled={rejecting === a._id}
                        onClick={function() { handleReject(a); }}
                        style={{
                          padding: '7px 14px', borderRadius: 8, border: '1.5px solid #dc2626',
                          background: '#fff', color: '#dc2626',
                          fontWeight: 700, fontSize: 12, cursor: rejecting === a._id ? 'default' : 'pointer',
                        }}
                      >{rejecting === a._id ? '\u2026' : '\u2715 Reject'}</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Bank Statement Ledger ── */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 24 }}>
            {/* Ledger Header */}
            <div style={{
              background: '#1e293b', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>{'\uD83D\uDCCB'} Transaction Ledger</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                {ledgerTotal} transaction(s) {'\u00B7'} Page {ledgerPage} of {Math.ceil(ledgerTotal / 50) || 1}
              </div>
            </div>

            {/* Column headers */}
            <div style={{
              display: 'grid', gridTemplateColumns: '90px 1fr 100px 90px 90px 90px',
              padding: '10px 18px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0',
              fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              <div>Date</div>
              <div>Description</div>
              <div>Reference</div>
              <div style={{ textAlign: 'right' }}>Credit ({'\u20B9'})</div>
              <div style={{ textAlign: 'right' }}>Debit ({'\u20B9'})</div>
              <div style={{ textAlign: 'right' }}>Balance</div>
            </div>

            {/* Rows */}
            {ledger.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                No transactions found for this period.
              </div>
            ) : (
              <div>
                {(function() {
                  var runBal = 0;
                  return ledger.slice().reverse().map(function(row, i) {
                    runBal += row.credit - row.debit;
                    var isEven = i % 2 === 0;
                    return (
                      <div key={row._id || i} style={{
                        display: 'grid', gridTemplateColumns: '90px 1fr 100px 90px 90px 90px',
                        padding: '10px 18px', fontSize: 13, color: '#334155',
                        background: isEven ? '#fff' : '#f8fafc',
                        borderBottom: '1px solid #f1f5f9',
                        fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                      }}>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{fmtDate(row.date)}</div>
                        <div style={{ fontFamily: 'inherit', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, marginRight: 6,
                            background: row.type === 'refund' ? '#fef2f2' : row.paymentMode === 'online' ? '#f5f3ff' : '#eff6ff',
                            color: row.type === 'refund' ? '#dc2626' : row.paymentMode === 'online' ? '#7c3aed' : '#2563eb',
                          }}>
                            {row.type === 'refund' ? 'REFUND' : (row.paymentMode || 'cash').toUpperCase()}
                          </span>
                          {row.description}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{row.reference}</div>
                        <div style={{ textAlign: 'right', color: row.credit > 0 ? '#059669' : '#d1d5db', fontWeight: row.credit > 0 ? 700 : 400 }}>
                          {row.credit > 0 ? fmtFull(row.credit) : '\u2014'}
                        </div>
                        <div style={{ textAlign: 'right', color: row.debit > 0 ? '#dc2626' : '#d1d5db', fontWeight: row.debit > 0 ? 700 : 400 }}>
                          {row.debit > 0 ? fmtFull(row.debit) : '\u2014'}
                        </div>
                        <div style={{ textAlign: 'right', fontWeight: 700, color: runBal >= 0 ? '#1e293b' : '#dc2626' }}>
                          {fmtFull(runBal)}
                        </div>
                      </div>
                    );
                  }).reverse();
                })()}
              </div>
            )}

            {/* Pagination */}
            {ledgerTotal > 50 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '12px 18px', borderTop: '1px solid #e2e8f0' }}>
                <button
                  disabled={ledgerPage <= 1}
                  onClick={function() { setLedgerPage(Math.max(1, ledgerPage - 1)); }}
                  style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: ledgerPage > 1 ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600 }}
                >{'\u2039'} Prev</button>
                <span style={{ padding: '6px 0', fontSize: 12, color: '#64748b' }}>
                  Page {ledgerPage} of {Math.ceil(ledgerTotal / 50)}
                </span>
                <button
                  disabled={ledgerPage >= Math.ceil(ledgerTotal / 50)}
                  onClick={function() { setLedgerPage(ledgerPage + 1); }}
                  style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: ledgerPage < Math.ceil(ledgerTotal / 50) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600 }}
                >Next {'\u203A'}</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
